import { z } from 'zod';
import { BOARD_CODES } from './enums.js';
/**
 * A board's syllabus as a data file: subjects, chapters and sub-topics.
 *
 * The syllabus is what every other piece of content hangs off — questions are
 * tagged to its chapters, practice items to its sub-topics, and the tutor's
 * topic pack is built per chapter — so it is loaded from one reviewed file
 * rather than typed into a seed. `padhai content syllabus` loads it into any
 * database, including a pilot's; the development seed reads the same file.
 *
 * Chapters are matched by name, not by number. Between syllabus editions the
 * numbers move (AP's chapter 2 was Sets and is now Polynomials), and matching
 * on the number would quietly turn one chapter's student history into
 * another's. A renamed chapter is therefore a new chapter, and the old one is
 * reported as no longer in the file.
 */
const name = z.string().trim().min(1).max(120);
export const syllabusChapterSchema = z.object({
    /** The chapter number as the textbook prints it. */
    ref: z.string().trim().min(1).max(20),
    name,
    subTopics: z.array(name).min(1).max(20),
});
export const syllabusSubjectSchema = z.object({
    code: z
        .string()
        .trim()
        .regex(/^[A-Z][A-Z0-9_]{0,15}$/, 'a subject code is upper case, like MATH or PS'),
    name,
    chapters: z.array(syllabusChapterSchema).min(1),
});
export const syllabusFileSchema = z
    .object({
    board: z.enum(BOARD_CODES),
    grade: z.number().int().min(1).max(12),
    boardName: name,
    /** Which edition of the syllabus this is, in words. */
    edition: z.string().trim().min(1),
    /** Where the structure was taken from, so a reviewer can check it. */
    source: z.string().trim().min(1),
    /** Whether a teacher has checked it against the textbooks. */
    reviewed: z.boolean(),
    subjects: z.array(syllabusSubjectSchema).min(1),
})
    .superRefine((file, context) => {
    const codes = new Set();
    file.subjects.forEach((subject, subjectIndex) => {
        if (codes.has(subject.code)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['subjects', subjectIndex, 'code'],
                message: `Subject ${subject.code} appears twice.`,
            });
        }
        codes.add(subject.code);
        // Name is the match key, so two chapters with one name would make
        // tagging and review import pick one of them silently.
        const chapters = new Set();
        subject.chapters.forEach((chapter, chapterIndex) => {
            const key = syllabusKey(chapter.name);
            if (chapters.has(key)) {
                context.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['subjects', subjectIndex, 'chapters', chapterIndex, 'name'],
                    message: `${subject.code} has two chapters called "${chapter.name}".`,
                });
            }
            chapters.add(key);
            const subTopics = new Set();
            chapter.subTopics.forEach((subTopic, subTopicIndex) => {
                const subKey = syllabusKey(subTopic);
                if (subTopics.has(subKey)) {
                    context.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: [
                            'subjects',
                            subjectIndex,
                            'chapters',
                            chapterIndex,
                            'subTopics',
                            subTopicIndex,
                        ],
                        message: `"${chapter.name}" lists "${subTopic}" twice.`,
                    });
                }
                subTopics.add(subKey);
            });
        });
    });
});
/**
 * How two names are compared: case, spacing and the dash a textbook happens to
 * print do not make a different chapter.
 */
export function syllabusKey(value) {
    return value
        .normalize('NFKC')
        .toLowerCase()
        .replace(/[‐-―−-]/g, '-')
        .replace(/\s+/g, ' ')
        .trim();
}
//# sourceMappingURL=syllabus.js.map